import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/date";
import { displayValue, firstDisplayValue, hasDisplayValue, joinDisplayValues } from "@/lib/display";
import StatusBadge from "@/components/common/StatusBadge";
import PriorityBadge from "@/components/common/PriorityBadge";
import SLATimer from "@/components/common/SLATimer";
import { STATUS_LABEL, VISIBLE_TICKET_DISPLAY_STATUSES, ROLES, PRIORITIES, PRIORITY_LABEL, SEVERITIES, hasPermission, isClientStatusRole, getClientDisplayStatus, getTicketDisplayStatus } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Paperclip, Download, Eye, Trash2, MessageSquare, ListChecks, History, Timer as TimerIcon, PlayCircle, CheckCircle2, XCircle, User2, RotateCcw, Pencil } from "lucide-react";
import TicketEditDialog from "@/components/tickets/TicketEditDialog";
import ConversationTimeline from "@/components/tickets/ConversationTimeline";

const CLIENT_INTAKE_ACTION_ROLES = [
  ROLES.BUSINESS_ADMIN,
  ROLES.SUPPORT_ADMIN,
  ROLES.SUPER_ADMIN,
  ROLES.TEST_USER,
];
const CLIENT_INTAKE_ACTION_LABELS = {
  in_progress: "Start",
  client_confirmation_pending: "Client Confirmation Pending",
  reference_pending: "Reference Pending",
  invalid: "No Issue",
  on_hold: "Hold",
};

/**
 * Role â†’ available "next status" actions. Keys are internal status; values are UI labels. AJ
 * We keep the set intentionally comprehensive so admins/super-admins can force transitions.
 */
function computeAllowedActions(
  role,
  current,
  clientHasResponded = false,
  holdClientHasResponded = false,
  investigationClientHasResponded = false,
  escalationClientHasResponded = false,
  accessClientHasResponded = false,
  vendorResponseRecorded = false,
  severity = "moderate",
  hasAssignedTestUser = false,
  canEngineerResumeOwnHold = false,
  onHoldPreviousStatus = "in_progress",
  referencePreviousStatus = "in_progress"
) {
  const actions = {};
  const add = (status, label) => (actions[status] = label);
  const isAdmin = [ROLES.SUPER_ADMIN, ROLES.SUPPORT_ADMIN, ROLES.BUSINESS_ADMIN].includes(role);
  const canAssign = [ROLES.SUPER_ADMIN, ROLES.SUPPORT_ADMIN, ROLES.BUSINESS_ADMIN, ROLES.TEST_USER].includes(role);

  if (current === "open" && canAssign) {
    add("in_progress", "Start");
    add("client_confirmation_pending", "Client Confirmation Pending");
    add("reference_pending", "Reference Pending");
    add("invalid", "No Issue");
    add("on_hold", "Hold");
    return actions;
  }

  if (["assigned", "reopened"].includes(current) && role === ROLES.SUPPORT_ENGINEER) {
    add("in_progress", "Active");
    add("dev_done", "Dev Done");
    add("on_hold", "Hold");
    return actions;
  }

  if (current === "assigned") {
    if ([ROLES.SUPER_ADMIN, ROLES.SUPPORT_ADMIN, ROLES.BUSINESS_ADMIN, ROLES.TEST_USER].includes(role)) {
      add("reference_pending", "Reference Pending");
      add("client_confirmation_pending", "Client Confirmation Pending");
      add("assigned", "Reassign To");
      return actions;
    }
    if (role === ROLES.SUPPORT_ENGINEER) {
      add("in_progress", "Active");
      add("dev_done", "Dev Done");
      add("on_hold", "Hold");
      return actions;
    }
  }

  if (current === "in_progress" && role === ROLES.SUPPORT_ENGINEER) {
    add("in_progress", "Active");
    add("dev_done", "Dev Done");
    add("on_hold", "Hold");
    return actions;
  }

  if (current === "dev_done" && role === ROLES.SUPPORT_ENGINEER) {
    add("in_progress", "Active");
    add("dev_done", "Dev Done");
    add("dev_testing", "Dev Testing");
    add("on_hold", "Hold");
    return actions;
  }

  if (current === "dev_testing" && role === ROLES.SUPPORT_ENGINEER) {
    add("in_progress", "Active");
    add("dev_done", "Dev Done");
    add("dev_testing", "Dev Testing");
    add("in_qa", "In QA");
    add("on_hold", "Hold");
    return actions;
  }

  if (["in_progress", "reopened"].includes(current)) {
    if (canAssign) add("assigned", "Assign To");
    if ([ROLES.SUPER_ADMIN, ROLES.SUPPORT_ADMIN, ROLES.BUSINESS_ADMIN, ROLES.TEST_USER].includes(role)) {
      add("client_confirmation_pending", "Client Confirmation Pending");
      add("reference_pending", "Reference Pending");
      add("on_hold", "Hold");
    }
    return actions;
  }

  if (["dev_done", "dev_testing"].includes(current) && [ROLES.SUPPORT_ENGINEER, ROLES.SUPER_ADMIN, ROLES.SUPPORT_ADMIN].includes(role)) {
    add("in_progress", "Active");
    add("dev_done", "Dev Done");
    add("dev_testing", "Dev Testing");
    if (current === "dev_testing") add("in_qa", "In QA");
    add("on_hold", "Hold");
    return actions;
  }

  if (current === "in_qa" && role === ROLES.TEST_USER) {
    add("resolved", "QA Pass");
    add("reopened", "QA Fail");
    return actions;
  }

  if (current === "in_qa" && isAdmin && !hasAssignedTestUser) {
    add("assign_tester", "Assign to Tester");
  }

  if (["client_confirmation_pending", "reference_pending", "on_hold"].includes(current)) {
    const canResume = current === "on_hold"
      ? (isAdmin || canEngineerResumeOwnHold)
      : current === "reference_pending"
        ? accessClientHasResponded
        : clientHasResponded;
    if (canResume && [ROLES.SUPPORT_ENGINEER, ROLES.SUPER_ADMIN, ROLES.SUPPORT_ADMIN, ROLES.BUSINESS_ADMIN, ROLES.TEST_USER].includes(role)) {
      const resumableStatuses = ["open", "assigned", "in_progress", "dev_done", "dev_testing", "in_qa"];
      const resumeStatus = current === "on_hold" && resumableStatuses.includes(onHoldPreviousStatus)
        ? onHoldPreviousStatus
        : current === "reference_pending" && resumableStatuses.includes(referencePreviousStatus)
          ? referencePreviousStatus
          : "in_progress";
      add(resumeStatus, current === "on_hold" ? "Start" : "Start / Resume Work");
      if (current !== "on_hold" && canAssign) add("assigned", "Assign To");
    }
  }

  if (current === "resolved" && isAdmin) add("completed", "Mark Completed");
  if (["completed", "invalid"].includes(current) && isAdmin) add("reopened", "Reopen Ticket");
  if (["completed", "invalid"].includes(current) && [ROLES.CLIENT_USER, ROLES.CLIENT_ADMIN].includes(role)) {
    add("reopened", "Reopen Ticket");
  }
  return actions;
}

function getActionPanelActions(role, actions) {
  return { ...actions };
}
function isCommentActivityEvent(event) {
  const type = String(event.event_type || event.type || "").toLowerCase();
  const summary = String(event.message || event.title || "").trim().toLowerCase();
  const toValue = event.to_value;
  return (
    type.includes("comment") ||
    type.includes("message") ||
    event.visibility != null ||
    toValue?.visibility != null ||
    (!event.event_type && event.body != null) ||
    /^(a )?(new )?comment (was )?(added|edited|updated|deleted)/.test(summary)
  );
}

function getTicketActivityEvents(role, events, ticket) {
  const isClientRole = [
    ROLES.CLIENT_USER,
    ROLES.CLIENT_ADMIN,
    ROLES.BUSINESS_USER,
  ].includes(role);
  const clientDevelopmentStatuses = new Set([
    "in_progress",
    "assigned",
    "accepted",
    "returned_to_engineer",
    "dev_done",
    "dev_testing",
    "in_qa",
    "resolved",
    "in_progress",
    "on_hold",
  ]);
  const normalizeClientStatusValue = (value) => {
    const status = value && typeof value === "object" ? value.status : value;
    if (!clientDevelopmentStatuses.has(status)) return value;
    return value && typeof value === "object"
      ? { ...value, status: "client_in_progress" }
      : "client_in_progress";
  };
  const normalizedEvents = events.map((event) => {
    const isNoIssueOutcome = ticket?.validation_outcome === "no_issue";
    const isRejectedStatus = String(event.event_type || "").toLowerCase() === "status.rejected";
    const eventType = String(event.event_type || "").toLowerCase();
    const isInternalDevelopmentStatus = eventType.startsWith("status.") &&
      clientDevelopmentStatuses.has(eventType.slice("status.".length));
    return {
      ...event,
      ...(isClientRole && isInternalDevelopmentStatus
        ? {
            event_type: "status.client_in_progress",
          }
        : {}),
      ...(isClientRole
        ? {
            from_value: normalizeClientStatusValue(event.from_value),
            to_value: normalizeClientStatusValue(event.to_value),
          }
        : {}),
      ...(isNoIssueOutcome && isRejectedStatus
        ? { event_type: "status.invalid", to_value: "invalid" }
        : {}),
      // Event messages can contain user-entered action comments or reasons.
      // Keep those in the separate Comments section, not Activity History.
      message: null,
    };
  });

  if (isClientRole) {
    return normalizedEvents.filter((event) => {
      const type = String(event.event_type || "").toLowerCase();
      return (
        !isCommentActivityEvent(event) &&
        (
          type === "ticket.created" ||
          type === "ticket.closed" ||
          type === "ticket.status_changed" ||
          type.startsWith("status.")
        )
      );
    });
  }
  return normalizedEvents.filter(
    (event) => !isCommentActivityEvent(event)
  );
}

function canAddTicketComment(role, status) {
  if (role === ROLES.CLIENT_USER) return true;
  if (role === ROLES.CLIENT_ADMIN) return !["completed", "invalid"].includes(status);
  return true;
}

function getUnavailableActionMessage(status, current) {
  if (status === "reopened") {
    return "Reopen Ticket is available only for completed or No Issue tickets with reopening permission.";
  }
  if (current === "open") {
    return "This ticket is Open. Assign it, start work, request client information, place it on hold, or mark it No Issue.";
  }
  return "This action is not currently permitted. Check the ticket status, required client response, ownership and your role permissions.";
}
function getTicketActionPermission(role, currentStatus, actionStatus) {
  const permissions = [];
  const openIntakeActions = [
    "in_progress",
    "client_confirmation_pending",
    "reference_pending",
    "on_hold",
    "invalid",
  ];
  if (currentStatus === "open" && openIntakeActions.includes(actionStatus)) {
    permissions.push("validate_tickets");
  } else if (
    currentStatus === "in_progress" &&
    role === ROLES.TEST_USER &&
    ["client_confirmation_pending", "reference_pending", "on_hold"].includes(actionStatus)
  ) {
    permissions.push("validate_tickets");
  } else if (currentStatus === "on_hold" && actionStatus === "open") {
    permissions.push("validate_tickets");
  } else if (
    currentStatus === "assigned" &&
    ["reference_pending", "client_confirmation_pending"].includes(actionStatus)
  ) {
    permissions.push("validate_tickets");
  } else if (actionStatus === "completed") {
    permissions.push("close_tickets");
  } else if (actionStatus === "reopened") {
    permissions.push(
      role === ROLES.TEST_USER && currentStatus === "in_qa"
        ? "validate_tickets"
        : "reopen_tickets"
    );
  } else if (["assigned", "assign_tester"].includes(actionStatus)) {
    permissions.push("assign_engineers");
  } else if (role === ROLES.TEST_USER || actionStatus === "invalid") {
    permissions.push("validate_tickets");
  } else {
    permissions.push("edit_tickets");
  }
  return [...new Set(permissions)];
}

function canOverrideTicketAction(role, ticket, userId) {
  const adminOverride = [ROLES.SUPER_ADMIN, ROLES.SUPPORT_ADMIN, ROLES.BUSINESS_ADMIN].includes(role) &&
    (["open", "in_qa"].includes(ticket.status) || Boolean(ticket.assigned_engineer_id));
  const testUserIntakeOverride = role === ROLES.TEST_USER && ticket.status === "open";
  const assignedTesterCriticalOverride = role === ROLES.TEST_USER &&
    ticket.severity === "critical" &&
    ticket.status === "in_qa" &&
    String(ticket.assigned_tester_id || "") === String(userId || "");
  return adminOverride || testUserIntakeOverride || assignedTesterCriticalOverride;
}

export default function TicketDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState("");
  const [commentSaving, setCommentSaving] = useState(false);
  const [qaFailSaving, setQaFailSaving] = useState(false);
  const [clientConfirmationSaving, setClientConfirmationSaving] = useState(false);
  const [referenceResponseSaving, setReferenceResponseSaving] = useState(false);
  const [visibility, setVisibility] = useState("public");
  const [actionModal, setActionModal] = useState(null); // { status, label }
  const [actionState, setActionState] = useState({ assignment_type: "", assigned_engineer_id: "", assigned_tester_id: "", priority: "", severity: "" });
  const [engineers, setEngineers] = useState([]);
  const [testers, setTesters] = useState([]);
  const [testersLoading, setTestersLoading] = useState(false);
  const [testersError, setTestersError] = useState("");
  const [testersReloadKey, setTestersReloadKey] = useState(0);
  const [tab, setTab] = useState("details");
  const [editOpen, setEditOpen] = useState(false);
  const fileRef = useRef();

  // Attachment preview/delete state
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewContentType, setPreviewContentType] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState(null);

  // PR / Deployment state
  const [prReference, setPrReference] = useState("");
  const [prUrl, setPrUrl] = useState("");
  const [prEditing, setPrEditing] = useState(false);
  const [prSaving, setPrSaving] = useState(false);
  const [mergeSaving, setMergeSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/tickets/${id}`);
      setDetail(data);
    } catch (e) {
      toast.error(formatApiError(e));
      navigate("/tickets");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  useEffect(() => { load(); }, [load]);

  // Other users can change ticket status while this detail page is open.
  // Refresh the activity feed while it is visible so admins and clients see
  // testing decisions without needing a full page reload.
  useEffect(() => {
    if (!["activity-history", "conversation", "comments"].includes(tab)) return undefined;

    let active = true;
    const refreshActivity = async () => {
      if (document.visibilityState === "hidden") return;
      try {
        if (["comments", "conversation"].includes(tab)) {
          const { data } = await api.get(`/tickets/${id}`);
          if (active) setDetail(data);
          return;
        }
        const { data } = await api.get(`/tickets/${id}/events`);
        if (!active) return;
        setDetail((current) => current ? {
          ...current,
          ticket: { ...current.ticket, status: data.status },
          events: data.events || [],
        } : current);
      } catch (error) {
        if (tab === "comments") {
          toast.error(formatApiError(error));
        }
        // Keep the page usable if a background refresh fails; the main ticket
        // load still reports access or network errors to the user.
      }
    };

    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") refreshActivity();
    };

    refreshActivity();
    const interval = window.setInterval(refreshActivity, 10000);
    document.addEventListener("visibilitychange", refreshWhenVisible);

    return () => {
      active = false;
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [id, tab]);

  useEffect(() => {
    if (
      user?.role &&
      [
        ROLES.SUPER_ADMIN,
        ROLES.SUPPORT_ADMIN,
        ROLES.BUSINESS_ADMIN,
        ROLES.TEST_USER,
      ].includes(user.role)
    ) {
      api.get("/users/assignable-engineers")
        .then((r) => setEngineers(r.data || []))
        .catch((error) => {
          console.error(
            "Unable to load assignable engineers:",
            error
          );
          setEngineers([]);
        });
    }
  }, [user, detail?.ticket?.status]);

  useEffect(() => {
    const canListTesters = [
      ROLES.SUPER_ADMIN,
      ROLES.SUPPORT_ADMIN,
      ROLES.BUSINESS_ADMIN,
      ROLES.TEST_USER,
    ].includes(user?.role) || (
      user?.role === ROLES.SUPPORT_ENGINEER &&
      ["in_qa", "dev_testing"].includes(detail?.ticket?.status) &&
      detail?.ticket?.assigned_engineer_id === (user?.id || user?._id)
    );
    if (!canListTesters) {
      setTesters([]);
      setTestersError("");
      setTestersLoading(false);
      return undefined;
    }
    let cancelled = false;
    setTestersLoading(true);
    setTestersError("");
    api.get("/users/assignable-testers")
      .then((r) => {
        if (!cancelled) setTesters(r.data || []);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("Unable to load assignable Test Users:", error);
        setTesters([]);
        setTestersError(formatApiError(error));
      })
      .finally(() => {
        if (!cancelled) setTestersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user, detail?.ticket?.status, detail?.ticket?.assigned_engineer_id, testersReloadKey]);

  if (loading || !detail) {
    return <TicketDetailSkeleton />;
  }

  const {
    ticket,
    client,
    testing_assignment_by: testingAssignmentBy,
    people = {},
    users = {},
    events,
    comments,
    approvals,
    worklogs,
    attachments,
  } = detail;
  const currentWorkflowStatus =
    getTicketDisplayStatus(ticket) || ticket.status;
  const clientName = client
    ? `${client.name}${client.deleted ? " (deleted client)" : ""}`
    : null;
  const businessAdmin =
    (people.business_owner?.role === "business_admin" &&
      people.business_owner) ||
    people.business_admins?.[0];
  const waitingForClientAt =
    ticket.waiting_for_client_at;

  const respondingClientId = user?.id || user?._id;
  const waitingForClientTime = new Date(waitingForClientAt || 0).getTime();
  const isAfterClientRequest = (createdAt) =>
    waitingForClientTime > 0 &&
    new Date(createdAt || 0).getTime() > waitingForClientTime;
  const clientHasResponded = Boolean(respondingClientId) && (
    comments.some((entry) =>
      entry.author_id === respondingClientId &&
      entry.visibility === "public" &&
      isAfterClientRequest(entry.created_at)
    ) ||
    attachments.some((entry) =>
      entry.uploaded_by === respondingClientId &&
      isAfterClientRequest(entry.created_at)
    )
  );

  const onHoldAt =
    ticket.on_hold_at;

  const holdClientRespondedAt =
    ticket.hold_client_responded_at;

  const holdClientHasResponded =
    Boolean(holdClientRespondedAt) &&
    (
      !onHoldAt ||
      holdClientRespondedAt > onHoldAt
    );

  const investigationStartedAt =
    ticket.investigation_started_at;

  const investigationClientRespondedAt =
    ticket.investigation_client_responded_at;

  const investigationClientHasResponded =
    Boolean(investigationClientRespondedAt) &&
    (
      !investigationStartedAt ||
      investigationClientRespondedAt >
        investigationStartedAt
    );

  const escalatedAt =
    ticket.escalated_at;

  const escalationClientRespondedAt =
    ticket.escalation_client_responded_at;

  const escalationClientHasResponded =
    Boolean(escalationClientRespondedAt) &&
    (
      !escalatedAt ||
      escalationClientRespondedAt >
        escalatedAt
    );

  const waitingForAccessAt =
    ticket.waiting_for_access_at;

  const accessClientRespondedAt =
    ticket.access_client_responded_at;

  const accessClientHasResponded =
    Boolean(accessClientRespondedAt) &&
    (
      !waitingForAccessAt ||
      accessClientRespondedAt > waitingForAccessAt
    );

  const actions = computeAllowedActions(
    user.role,
    currentWorkflowStatus,
    clientHasResponded,
    holdClientHasResponded,
    investigationClientHasResponded,
    escalationClientHasResponded,
    accessClientHasResponded,
    false,
    ticket.severity,
    people.assigned_tester?.role === ROLES.TEST_USER,
    String(ticket.on_hold_by || "") === String(user?.id || user?._id || ""),
    ticket.on_hold_previous_status || "in_progress",
    ticket.waiting_for_access_previous_status || "in_progress"
  );

  const raisedByRole = people.raised_by?.role;
  const isOpenIntakeTicket = currentWorkflowStatus === "open";
  if (
    isOpenIntakeTicket &&
    CLIENT_INTAKE_ACTION_ROLES.includes(user.role)
  ) {
    Object.keys(actions).forEach((status) => delete actions[status]);
    Object.assign(actions, CLIENT_INTAKE_ACTION_LABELS);
  }
  if (raisedByRole && !isClientStatusRole(raisedByRole)) {
    delete actions.client_confirmation_pending;
    delete actions.reference_pending;
  }

  const currentUserId = user?.id || user?._id;

  if (actions.assign_tester && ticket.assigned_tester_id) {
    actions.assign_tester = "Reassign Tester";
  }
  if (
    user.role === ROLES.TEST_USER &&
    ticket.status === "in_qa" &&
    String(ticket.assigned_tester_id || "") !== String(currentUserId || "")
  ) {
    Object.keys(actions).forEach((status) => delete actions[status]);
  }
  const assignedEngineerId =
    ticket.assigned_engineer_id;

  const hasEngineerAssignment =
    Boolean(assignedEngineerId);

  const isAssignedEngineer =
    user.role === ROLES.SUPPORT_ENGINEER &&
    currentUserId === assignedEngineerId;
  const isAssignedQaTester =
    user.role === ROLES.TEST_USER &&
    ticket.status === "in_qa" &&
    String(ticket.assigned_tester_id || "") === String(currentUserId || "");
  const isTesterReturnedToEngineerAfterQaFailure =
    user.role === ROLES.TEST_USER &&
    ticket.qa_failed_returned_to_engineer === true;

  const isNonOwningEngineer =
    user.role === ROLES.SUPPORT_ENGINEER &&
    hasEngineerAssignment &&
    !isAssignedEngineer;

  const isAssignedBusinessUser =
    user.role === ROLES.BUSINESS_USER &&
    hasEngineerAssignment;

  const isSupervisingAssignedAdmin =
    hasEngineerAssignment &&
    user.role === ROLES.BUSINESS_ADMIN;

  const isUnassignedSupportActor =
    !hasEngineerAssignment &&
    user.role === ROLES.SUPPORT_ENGINEER;

  const isTicketRaiser =
    Boolean(currentUserId) &&
    ticket.raised_by === currentUserId;

  const isClientActor = [
    ROLES.CLIENT_USER,
    ROLES.CLIENT_ADMIN,
  ].includes(user.role);
  if (
    isClientActor &&
    isTicketRaiser &&
    ticket.status === "client_confirmation_pending"
  ) {
    actions.client_confirmation_submit = "Submit";
  }
  if (
    isClientActor &&
    (user.role === ROLES.CLIENT_ADMIN || isTicketRaiser) &&
    ticket.status === "reference_pending"
  ) {
    actions.reference_submit = "Submit";
  }
  const clientAwaitingValidation =
    isClientActor && currentWorkflowStatus === "open";
  const clientInternalDevelopmentStage = isClientActor && [
    "assigned",
    "accepted",
    "in_progress",
    "returned_to_engineer",
    "dev_done",
    "dev_testing",
    "in_qa",
    "resolved",
    "in_progress",
    "on_hold",
  ].includes(ticket.status);
  const displayStatus = isClientStatusRole(user.role)
    ? getClientDisplayStatus(ticket)
    : getTicketDisplayStatus(ticket);
  const isClientUser = user.role === ROLES.CLIENT_USER;
  const activityEvents = getTicketActivityEvents(user.role, events, ticket);

  const canAccessConversation = canAddTicketComment(user.role, ticket.status);

  // Client Admin can reopen an Invalid ticket for their organization.
  // Other close/reopen actions remain limited to the original raiser.
  const canClientReopenInvalid =
    isClientActor && ticket.status === "invalid";
  if (isClientActor && !isTicketRaiser && !canClientReopenInvalid) {
    delete actions.closed;
    delete actions.reopened;
  }

  // Unassigned Support Engineers remain view-only. Support Admins
  // retain workflow control and can assign engineers after validation.
  if (isUnassignedSupportActor) {
    Object.keys(actions).forEach((key) => delete actions[key]);
  }

  // Explicit assignment creates exclusive engineer ownership.
  // Other engineers and Business Users become view-only while
  // Business Admin / Support Admin / Super Admin retain control.
  if (
    isNonOwningEngineer ||
    isAssignedBusinessUser
  ) {
    Object.keys(actions).forEach((key) => {
      delete actions[key];
    });
  }

  // Once assigned, Business Admin and Support Admin retain
  // workflow control while Business Admin may also reassign
  // the engineer.
  if (isSupervisingAssignedAdmin) {
    actions.assigned = "Reassign To";
  }

  // Roles & Permissions matrix: hide actions the role has been denied.
  if (!hasPermission(user, "assign_engineers")) {
    delete actions.assign_tester;
  }

  Object.keys(actions).forEach((status) => {
    const permission = getTicketActionPermission(user.role, currentWorkflowStatus, status);
    if (permission.some((key) => !hasPermission(user, key))) delete actions[status];
  });

  const panelActions = getActionPanelActions(user.role, actions);
  if (isTesterReturnedToEngineerAfterQaFailure || isUnassignedSupportActor || isNonOwningEngineer || isAssignedBusinessUser || clientAwaitingValidation) {
    Object.keys(panelActions).forEach((status) => delete panelActions[status]);
  }
  if (isSupervisingAssignedAdmin && panelActions.assigned) {
    panelActions.assigned = "Reassign To";
  }
  Object.keys(panelActions).forEach((status) => {
    const isWorkflowOperation = [
      "assign_tester",
      "client_confirmation_submit",
      "reference_submit",
    ].includes(status);
    if (!isWorkflowOperation && !VISIBLE_TICKET_DISPLAY_STATUSES.has(getTicketDisplayStatus({ status }))) {
      delete panelActions[status];
    }
    const permission = getTicketActionPermission(user.role, currentWorkflowStatus, status);
    if (permission.some((key) => !hasPermission(user, key))) delete panelActions[status];
  });
  const canShowClientIntakeActions =
    isOpenIntakeTicket &&
    CLIENT_INTAKE_ACTION_ROLES.includes(user.role);
  if (canShowClientIntakeActions) {
    Object.keys(panelActions).forEach((status) => delete panelActions[status]);
    Object.assign(panelActions, CLIENT_INTAKE_ACTION_LABELS);
  }
  const roleCanEdit = [
    ROLES.SUPER_ADMIN,
    ROLES.SUPPORT_ADMIN,
    ROLES.SUPPORT_ENGINEER,
    ROLES.BUSINESS_ADMIN,
    ROLES.BUSINESS_USER,
    ROLES.CLIENT_ADMIN,
    ROLES.CLIENT_USER,
  ].includes(user.role);

  const canEdit =
    roleCanEdit &&
    hasPermission(user, "edit_tickets") &&
    !isUnassignedSupportActor &&
    !isNonOwningEngineer &&
    !isAssignedBusinessUser &&
    !isSupervisingAssignedAdmin &&
    !clientAwaitingValidation;

  const isSupportTeam = [ROLES.SUPER_ADMIN, ROLES.SUPPORT_ADMIN, ROLES.SUPPORT_ENGINEER].includes(user.role);
  const canUseBusinessAdminVisibility = isSupportTeam || user.role === ROLES.BUSINESS_ADMIN;
  const openAction = (status, label) => {
    const isNoIssue = status === "no_issue" || status === "invalid";
    const targetStatus = isNoIssue ? "invalid" : status;
    const requiredPermissions = getTicketActionPermission(user.role, currentWorkflowStatus, targetStatus);
    if (requiredPermissions.some((permission) => !hasPermission(user, permission))) {
      toast.error("Your role does not have permission to perform this action.");
      return;
    }
    const workflowTicket = { ...ticket, status: currentWorkflowStatus };
    const workflowOverride = canOverrideTicketAction(user.role, workflowTicket, currentUserId);
    if ((!actions[targetStatus] && !(status === "no_issue" && actions.rejected) && !workflowOverride) || (status === "no_issue" && currentWorkflowStatus !== "open")) {
      if (isNoIssue) {
        toast.info("No Issue is available only during ticket validation. It confirms the product works as expected.");
        return;
      }
      toast.info(getUnavailableActionMessage(status, currentWorkflowStatus));
      return;
    }
    setActionState({
      assignment_type: "",
      assigned_engineer_id: "",
      assigned_tester_id: "",
      priority: "",
      severity: "",
    });
    const resumableStatuses = ["open", "assigned", "in_progress", "dev_done", "dev_testing", "in_qa"];
    const holdResumeTarget = resumableStatuses.includes(ticket.on_hold_previous_status)
      ? ticket.on_hold_previous_status
      : "in_progress";
    const prRequiredWarning =
      targetStatus === "dev_done" &&
      [ROLES.SUPPORT_ENGINEER, ROLES.SUPPORT_ADMIN].includes(user.role) &&
      (!ticket.pr_reference || !ticket.pr_url);
    setActionModal({
      status: targetStatus,
      label,
      isNoIssue,
      isResume: ticket.status === "on_hold" && targetStatus === holdResumeTarget,
      prRequiredWarning,
    });
  };

  const raisePR = async () => {
    if (!prReference.trim()) {
      return toast.error("PR reference is required");
    }

    if (!prUrl.trim()) {
      return toast.error("PR URL is required");
    }

    let parsedPrUrl;
    try {
      parsedPrUrl = new URL(prUrl.trim());
    } catch {
      return toast.error("Please enter a valid PR URL");
    }

    if (!["http:", "https:"].includes(parsedPrUrl.protocol) || !parsedPrUrl.hostname) {
      return toast.error("Please enter a valid PR URL starting with https:// or http://");
    }

    setPrSaving(true);

    try {
      const updatingExistingPR = Boolean(ticket.pr_reference);
      await api.post(`/tickets/${id}/pr`, {
        pr_reference: prReference.trim(),
        pr_url: prUrl.trim() || undefined,
      });

      toast.success(updatingExistingPR ? "PR details updated" : "PR raised successfully");
      window.dispatchEvent(new Event("helpdesk-ticket-changed"));
      setPrEditing(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPrSaving(false);
    }
  };

  const mergePR = async () => {
    if (!ticket.pr_reference) {
      return toast.error("No PR has been raised for this ticket");
    }

    setMergeSaving(true);

    try {
      await api.post(`/tickets/${id}/pr/merge`, {
        merged: true,
      });

      toast.success("PR marked as merged");
      window.dispatchEvent(new Event("helpdesk-ticket-changed"));
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setMergeSaving(false);
    }
  };

  const canManagePRDeployment =
    (
      user.role === ROLES.SUPPORT_ADMIN &&
      hasPermission(user, "manage_pr")
    ) ||
    (
      user.role === ROLES.SUPPORT_ENGINEER &&
      ticket.assigned_engineer_id === currentUserId &&
      hasPermission(user, "manage_pr")
    );
  const canManagePR =
    canManagePRDeployment ||
    (user.role === ROLES.PR_ADMIN && hasPermission(user, "manage_pr"));
  const canEditPROnReopened =
    ticket.status === "reopened" && canManagePRDeployment;

  const canViewPRDetails = ![
    ROLES.CLIENT_ADMIN,
    ROLES.CLIENT_USER,
    ROLES.BUSINESS_USER,
  ].includes(user.role);

  const submitAction = async () => {
    if (!actionModal) return;

    if (actionModal.isResume) {
      try {
        const { data: updatedTicket } = await api.post(`/tickets/${id}/status`, {
          status: actionModal.status,
          reason: "Work resumed from Hold.",
        });
        toast.success(`Ticket resumed to ${STATUS_LABEL[updatedTicket?.status || actionModal.status]}`);
        window.dispatchEvent(new Event("helpdesk-ticket-changed"));
        setActionModal(null);
        await load();
      } catch (e) {
        toast.error(formatApiError(e));
      }
      return;
    }

    if (actionModal.status === "in_qa") {
      if (!actionState.assigned_tester_id) return toast.error("Pick a tester");
      try {
        await api.post(`/tickets/${id}/testing-assignment`, {
          tester_id: actionState.assigned_tester_id,
        });
        toast.success("Tester assigned and ticket moved to In QA");
        window.dispatchEvent(new Event("helpdesk-ticket-changed"));
        setActionModal(null);
        await load();
      } catch (e) {
        toast.error(formatApiError(e));
      }
      return;
    }

    if (actionModal.status === "assigned") {
      if (!actionState.assignment_type) {
        return toast.error("Choose Tester or Engineer");
      }
      if (actionState.assignment_type === "tester") {
        if (!actionState.assigned_tester_id) return toast.error("Pick a tester");
        try {
          await api.post(`/tickets/${id}/testing-assignment`, {
            tester_id: actionState.assigned_tester_id,
          });
          toast.success("Ticket assigned to Test User");
          window.dispatchEvent(new Event("helpdesk-ticket-changed"));
          setActionModal(null);
          await load();
        } catch (e) {
          toast.error(formatApiError(e));
        }
        return;
      }
      if (!actionState.assigned_engineer_id) return toast.error("Pick an engineer");
    }

    if (actionModal.status === "assign_tester") {
      if (!actionState.assigned_tester_id) return toast.error("Pick a tester");
      try {
        await api.post(`/tickets/${id}/testing-assignment`, {
          tester_id: actionState.assigned_tester_id,
        });
        toast.success("Ticket assigned for testing");
        window.dispatchEvent(new Event("helpdesk-ticket-changed"));
        setActionModal(null);
        load();
      } catch (e) {
        toast.error(formatApiError(e));
      }
      return;
    }

    const actionReasons = {
      assigned: "Ticket assigned to an engineer.",
      client_confirmation_pending: "Additional client confirmation requested.",
      reference_pending: "Reference or access details requested from the client.",
      on_hold: "Ticket placed on hold.",
      rejected: "Ticket rejected during validation.",
      invalid: "Ticket marked as no issue during validation.",
      reopened: "Ticket reopened for further work.",
      resolution_submitted: "Resolution submitted for review.",
      resolution_rejected: "Further development is required.",
      resolved: "QA passed.",
      completed: "Ticket completed.",
      closed: "Ticket completed.",
      in_progress: "Work started or resumed.",
    };
    const actionReason = actionModal.isNoIssue
      ? "Ticket marked as no issue during validation."
      : actionReasons[actionModal.status] ||
        `Ticket status updated to ${STATUS_LABEL[actionModal.status] || actionModal.status}.`;

    const payload = {
      status: actionModal.status,
      reason: actionReason,
    };
    if (
      [ROLES.TEST_USER, ROLES.SUPPORT_ADMIN, ROLES.BUSINESS_ADMIN, ROLES.SUPER_ADMIN].includes(user.role) &&
      ["rejected", "invalid"].includes(actionModal.status)
    ) {
      payload.validation_decision = actionModal.isNoIssue ? "no_issue" : "rejected";
    }
    if (actionModal.status === "assigned") {
      payload.assigned_engineer_id = actionState.assigned_engineer_id;
    }
    if (actionState.priority) payload.priority = actionState.priority;
    if (actionState.severity) payload.severity = actionState.severity;
    try {
      const { data: updatedTicket } = await api.post(`/tickets/${id}/status`, payload);
      toast.success(
        actionModal.isNoIssue
          ? "Validated as No Issue - Working as Expected"
          : actionModal.status === "resolution_approved"
            ? "Resolution accepted"
            : `Status updated to ${STATUS_LABEL[updatedTicket?.status || actionModal.status]}`
      );
      window.dispatchEvent(new Event("helpdesk-ticket-changed"));
      setActionModal(null);
      if (
        user.role === ROLES.TEST_USER &&
        ["submitted", "rejected", "invalid"].includes(actionModal.status)
      ) {
        setTab("activity-history");
      }
      await load();
    } catch (e) {
      // A status update may be saved before a downstream notification fails.
      // Refresh so the ticket and its activity timeline stay in sync.
      await load();
      toast.error(formatApiError(e));
    } finally {
      setPrSaving(false);
    }
  };

  const failQaAndReopen = async () => {
    if (qaFailSaving) return;
    setQaFailSaving(true);
    try {
      const { data: updatedTicket } = await api.post(`/tickets/${id}/status`, {
        status: "reopened",
        reason: "QA failed; returning the ticket to the assigned engineer.",
      });
      toast.success(`Ticket status updated to ${STATUS_LABEL[updatedTicket?.status || "reopened"]}`);
      window.dispatchEvent(new Event("helpdesk-ticket-changed"));
      await load();
    } catch (error) {
      await load();
      toast.error(formatApiError(error));
    } finally {
      setQaFailSaving(false);
    }
  };

  const submitClientConfirmation = async () => {
    if (clientConfirmationSaving) return;
    if (!clientHasResponded) {
      toast.error("Please provide either a comment or a file to resume the flow");
      return;
    }
    setClientConfirmationSaving(true);
    try {
      const { data: updatedTicket } = await api.post(
        `/tickets/${id}/client-confirmation`,
      );
      toast.success(
        `Ticket status updated to ${STATUS_LABEL[updatedTicket?.status] || "previous status"}`,
      );
      window.dispatchEvent(new Event("helpdesk-ticket-changed"));
      await load();
    } catch (error) {
      await load();
      toast.error(formatApiError(error));
    } finally {
      setClientConfirmationSaving(false);
    }
  };

  const submitReferenceResponse = async () => {
    if (referenceResponseSaving) return;
    if (!accessClientHasResponded) {
      toast.error("Please provide either a comment or a file before submitting the reference");
      return;
    }
    setReferenceResponseSaving(true);
    try {
      const { data: updatedTicket } = await api.post(
        `/tickets/${id}/reference-response`,
      );
      toast.success(
        `Ticket status updated to ${STATUS_LABEL[updatedTicket?.status] || "previous status"}`,
      );
      window.dispatchEvent(new Event("helpdesk-ticket-changed"));
      await load();
    } catch (error) {
      await load();
      toast.error(formatApiError(error));
    } finally {
      setReferenceResponseSaving(false);
    }
  };

  const addComment = async () => {
    if (!comment.trim() || commentSaving) return;
    setCommentSaving(true);
    try {
      const commentVisibility =
        visibility === "business_admin" ? "business_admin" : "public";
      await api.post(`/tickets/${id}/comments`, {
        body: comment.trim(),
        visibility: commentVisibility,
      });
      setComment("");
      toast.success("Comment added");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCommentSaving(false);
    }
  };

  const previewFile = async (attachment) => {
    setPreviewAttachment(attachment);
    setPreviewUrl("");
    setPreviewContentType(attachment.content_type || "");
    setPreviewLoading(true);

    try {
      const response = await api.get(
        `/attachments/${attachment.id}/download`,
        { responseType: "blob" }
      );

      const contentType =
        response.headers?.["content-type"] ||
        attachment.content_type ||
        "application/octet-stream";

      const url = URL.createObjectURL(response.data);

      setPreviewContentType(contentType);
      setPreviewUrl(url);
    } catch (e) {
      setPreviewAttachment(null);
      setPreviewUrl("");
      toast.error(formatApiError(e));
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    setPreviewUrl("");
    setPreviewContentType("");
    setPreviewAttachment(null);
    setPreviewLoading(false);
  };

  const deleteAttachment = async (attachment) => {
    const confirmed = window.confirm(
      `Delete "${attachment.original_name}"? This file will be permanently removed.`
    );

    if (!confirmed) return;

    setDeletingAttachmentId(attachment.id);

    try {
      await api.delete(`/attachments/${attachment.id}`);

      setDetail((current) => {
        if (!current) return current;

        return {
          ...current,
          attachments: (current.attachments || []).filter(
            (item) => item.id !== attachment.id
          ),
        };
      });

      toast.success(`"${attachment.original_name}" deleted`);
      window.dispatchEvent(new Event("helpdesk-ticket-changed"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeletingAttachmentId(null);
    }
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/tickets/${id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Attachment uploaded");
      window.dispatchEvent(new Event("helpdesk-ticket-changed"));
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-6" data-testid="ticket-detail-page">
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <button
                type="button"
                onClick={() => navigate("/")}
                className="transition-colors hover:text-foreground"
              >
                Dashboard
              </button>
            </BreadcrumbLink>
          </BreadcrumbItem>

          <BreadcrumbSeparator />

          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <button
                type="button"
                onClick={() => navigate("/tickets")}
                className="transition-colors hover:text-foreground"
              >
                Tickets
              </button>
            </BreadcrumbLink>
          </BreadcrumbItem>

          <BreadcrumbSeparator />

          <BreadcrumbItem>
            <BreadcrumbPage>
              {ticket.ticket_number}
            </BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="glass rounded-xl p-6 border border-border">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground">
              <span data-testid="ticket-number">{ticket.ticket_number}</span>
              <PriorityBadge priority={ticket.priority} />
              <StatusBadge status={displayStatus} />
            </div>
            <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight mt-2" data-testid="ticket-title">
              {ticket.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-muted-foreground">
              <Meta label="Client" value={clientName} />
              <Meta label="Raised by" value={people.raised_by?.name} />
              <Meta label="Severity" value={ticket.severity} />
              <Meta label="Type" value={ticket.ticket_type?.replace(/_/g, " ")} />
              <Meta label="Reopens" value={ticket.reopen_count} />
              <Meta label="Created" value={formatDateTime(ticket.created_at)} />
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <SLATimer
              deadline={ticket.first_response_due}
              label="First response"
              status={displayStatus}
              completedAt={ticket.closed_at || ticket.resolved_at}
            />
            <SLATimer
              deadline={ticket.resolution_due}
              label="Resolution"
              status={displayStatus}
              completedAt={ticket.closed_at || ticket.resolved_at}
            />
            <div className="flex flex-col gap-1 mt-1">
              {canEdit && (
                <Button data-testid="ticket-edit-open" onClick={() => setEditOpen(true)} variant="outline" size="sm" className="justify-start">
                  <Pencil className="h-3.5 w-3.5 mr-1" /> Edit Fields
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        <Tabs value={tab} onValueChange={setTab} className="space-y-4">
          <TabsList className="bg-secondary/60">
            <TabsTrigger value="details" data-testid="tab-details"><ListChecks className="h-3.5 w-3.5 mr-1" /> Details</TabsTrigger>
            {(canAccessConversation || comments.length > 0) && !isClientUser && (
              <TabsTrigger value="conversation" data-testid="tab-conversation">
                <MessageSquare className="h-3.5 w-3.5 mr-1" /> {isClientActor ? "Conversation" : "Comments"} ({comments.length})
              </TabsTrigger>
            )}
            <TabsTrigger value="activity-history" data-testid="tab-activity-history">
              <History className="h-3.5 w-3.5 mr-1" /> Activity History ({activityEvents.length})
            </TabsTrigger>
            {isClientUser ? (
              <TabsTrigger value="comments" data-testid="tab-comments"><MessageSquare className="h-3.5 w-3.5 mr-1" /> Comments ({comments.length})</TabsTrigger>
            ) : (
              <TabsTrigger value="worklogs" data-testid="tab-worklogs"><TimerIcon className="h-3.5 w-3.5 mr-1" /> Worklogs ({worklogs.length})</TabsTrigger>
            )}
            <TabsTrigger value="attachments" data-testid="tab-attachments"><Paperclip className="h-3.5 w-3.5 mr-1" /> Files ({attachments.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="rounded-xl border border-border bg-card p-5 space-y-4">
            <Section title="Description">
              <div className="whitespace-pre-wrap">{ticket.description || <Empty />}</div>
            </Section>

            <Section title="Business impact">
              <div className="whitespace-pre-wrap">{ticket.business_impact || <Empty />}</div>
            </Section>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              <section className="rounded-xl border border-border bg-muted/40 p-4">
                <div className="mb-3 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  Client
                </div>
                <div className="font-semibold">{displayValue(clientName)}</div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {displayValue(client?.code)}
                </div>
                <div className="mt-3 space-y-1 text-xs">
                  <div>{displayValue(firstDisplayValue(client?.primary_contact?.name, client?.contact_person))}</div>
                  <div className="text-muted-foreground">
                    {displayValue(firstDisplayValue(client?.primary_contact?.email, client?.contact_email))}
                  </div>
                  <div className="text-muted-foreground">
                    {displayValue(firstDisplayValue(client?.primary_contact?.phone, client?.contact_phone))}
                  </div>
                </div>
              </section>

              <section className="rounded-xl border border-border bg-muted/40 p-4">
                <div className="mb-3 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  Ownership
                </div>
                <div className="space-y-2 text-xs">
                  <Row k="Raised by" v={<Person person={people.raised_by} />} />
                  <Row
                    k="Client Admin"
                    v={<Person person={people.client_admins?.[0]} />}
                  />
                  <Row
                    k="Business Admin"
                    v={<Person person={businessAdmin} />}
                  />
                  <Row
                    k="Current owner"
                    v={<Person person={people.current_owner} />}
                  />
                </div>
              </section>

              <section className="rounded-xl border border-border bg-muted/40 p-4">
                <div className="mb-3 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  Support assignment
                </div>
                <div className="space-y-2 text-xs">
                  <Row
                    k="Test User"
                    v={<Person person={people.assigned_tester} />}
                  />
                  <Row
                    k="Engineer"
                    v={<Person person={people.assigned_engineer} />}
                  />
                  <Row
                    k="Resolved by"
                    v={<Person person={people.resolved_by} />}
                  />
                  <Row
                    k="Status"
                    v={STATUS_LABEL[displayStatus] || displayStatus}
                  />
                </div>
              </section>
            </div>

            {canViewPRDetails && (
              <>
                {/* PR / Merge */}
                <section id="ticket-pr-section" className="rounded-xl border border-border bg-muted/40 p-4 space-y-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  Pull Request & Merge
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Raise the PR before submitting the resolution. PR Admins are notified automatically.
                </div>
              </div>

              {ticket.pr_reference ? (
                !prEditing && (
                  <>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <Row k="PR Reference" v={ticket.pr_reference} />
                      <Row k="PR Status" v={ticket.pr_merged_at || ticket.pr_status === "merged" ? "Merged" : "Awaiting merge"} />
                      <Row
                        k="Raised by"
                        v={
                          users[ticket.pr_raised_by]?.name ||
                          ticket.pr_raised_by_name ||
                          "Unknown user"
                        }
                      />
                      <Row
                        k="Raised at"
                        v={ticket.pr_raised_at ? formatDateTime(ticket.pr_raised_at) : null}
                      />
                      <Row
                        k="Merged at"
                        v={ticket.pr_merged_at ? formatDateTime(ticket.pr_merged_at) : null}
                      />

                      {ticket.pr_url && (
                        <div className="md:col-span-2">
                          <div className="text-xs text-muted-foreground mb-1">PR URL</div>
                          <a
                            href={ticket.pr_url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-sm break-all text-primary hover:underline"
                          >
                            {ticket.pr_url}
                          </a>
                        </div>
                      )}
                    </div>
                  </>
                )
              ) : null}
              {!prEditing && (canEditPROnReopened || (ticket.pr_reference && !ticket.pr_merged_at && canManagePR)) && (
                <div className="flex flex-wrap gap-2">
                  {canEditPROnReopened && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setPrReference(ticket.pr_reference || "");
                        setPrUrl(ticket.pr_url || "");
                        setPrEditing(true);
                      }}
                    >
                      Edit PR
                    </Button>
                  )}
                  {ticket.pr_reference && !ticket.pr_merged_at && canManagePR && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={mergePR}
                      disabled={mergeSaving}
                    >
                      {mergeSaving ? "Saving..." : "Mark PR as Merged"}
                    </Button>
                  )}
                </div>
              )}
              {(!ticket.pr_reference || prEditing) && canManagePR ? (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_1fr_auto] items-end">
                  <div>
                    <Label>
                      PR Reference <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      value={prReference}
                      onChange={(e) => setPrReference(e.target.value)}
                      placeholder="PR #123"
                      required
                    />
                  </div>

                  <div>
                    <Label>
                      PR URL <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      type="url"
                      value={prUrl}
                      onChange={(e) => setPrUrl(e.target.value)}
                      placeholder="https://github.com/..."
                      required
                    />
                  </div>

                  <Button
                    onClick={raisePR}
                    disabled={prSaving}
                    className="bg-primary text-primary-foreground"
                  >
                    {prSaving ? "Saving..." : ticket.pr_reference ? "Update PR" : "Raise PR"}
                  </Button>
                </div>
              ) : !ticket.pr_reference && !canManagePR ? (
                <div className="text-sm text-muted-foreground">
                  No pull request has been recorded.
                </div>
              ) : null}

              {prEditing && (
                <Button
                  type="button"
                  variant="ghost"
                  disabled={prSaving}
                  onClick={() => setPrEditing(false)}
                >
                  Cancel PR edit
                </Button>
              )}

                </section>
              </>
            )}

          </TabsContent>

          {(isClientUser || canAccessConversation || comments.length > 0) && (
            <TabsContent value={isClientUser ? "comments" : "conversation"} className="rounded-xl border border-border bg-card p-5">
            <div className="space-y-6">
              {ticket.status === "reference_pending" && isClientActor && (
                <div
                  className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4"
                  data-testid="reference-pending-client-panel"
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 shrink-0 text-amber-700">
                      <Paperclip className="h-5 w-5" />
                    </div>

                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm font-semibold text-foreground">
                        {STATUS_LABEL.reference_pending}
                      </h3>

                      <p className="mt-1 text-xs leading-5 text-muted-foreground">
                        Please provide the requested reference, access details,
                        or supporting information so the support team can
                        continue this ticket.
                      </p>

                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        <span className="text-[11px] text-muted-foreground">
                          Add the reference in the conversation below, or upload
                          the requested document.
                        </span>

                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => setTab("attachments")}
                          data-testid="reference-pending-upload"
                          className="h-8"
                        >
                          <Paperclip className="mr-1.5 h-3.5 w-3.5" />
                          Upload Reference
                        </Button>
                      </div>

                      <div className="mt-2 text-[11px] font-medium text-amber-800">
                        After providing the reference, select Submit in the
                        Actions panel to resume the ticket.
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <ConversationTimeline
                ticket={null}
                comments={comments}
                onChanged={load}
              />

              {canAccessConversation ? (
              <div className="rounded-xl border border-border bg-secondary/20 p-4 space-y-3">
                <div>
                  <h3 className="text-sm font-semibold">{isClientActor ? "Add to conversation" : "Add a comment"}</h3>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Add a comment or provide the requested reference details.
                  </p>
                </div>

                <Textarea
                  data-testid="new-comment-body"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={4}
                  placeholder={
                    visibility === "business_admin"
                      ? "Write a note for Business Admins..."
                      : "Write a comment..."
                  }
                />

                <div className="flex justify-end">
                  <Button
                    data-testid="submit-comment"
                    onClick={addComment}
                    disabled={!comment.trim() || commentSaving}
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                  >
                    <MessageSquare className="mr-2 h-4 w-4" />
                    {commentSaving ? "Sending..." : isClientActor ? "Send Message" : "Add Comment"}
                  </Button>
                </div>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">Comments are read-only at this ticket stage.</p>
              )}
            </div>
          </TabsContent>
          )}

          <TabsContent
            value="activity-history"
            className="rounded-xl border border-border bg-card p-5 space-y-4"
          >
            <div>
              <h2 className="text-lg font-semibold">Activity History</h2>
              <p className="mt-1 text-xs text-muted-foreground">
                Complete system activity history for this ticket, including
                attachments, status changes, assignments and other ticket events.
              </p>
            </div>

            <Timeline
              events={activityEvents}
              attachments={attachments}
              clientStatusView={isClientStatusRole(user.role)}
            />
          </TabsContent>

          {!isClientUser && <TabsContent value="worklogs" className="rounded-xl border border-border bg-card p-5 space-y-3">
            <WorklogsPane ticketId={id} worklogs={worklogs} onChanged={load} canLog={[ROLES.SUPPORT_ENGINEER, ROLES.SUPPORT_ADMIN, ROLES.SUPER_ADMIN].includes(user.role)} />
          </TabsContent>}

          <TabsContent value="attachments" className="rounded-xl border border-border bg-card p-5 space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-xs text-muted-foreground">Upload evidence: screenshots, logs, PDFs, docs, videos.</div>
              <div>
                <input ref={fileRef} type="file" onChange={uploadFile} className="hidden" id="ticket-file-input" data-testid="ticket-file-input" />
                <Button asChild variant="outline" data-testid="upload-attachment"><label htmlFor="ticket-file-input" className="cursor-pointer">Upload file</label></Button>
              </div>
            </div>
            {attachments.length === 0 && <div className="text-xs text-muted-foreground">No files uploaded yet.</div>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {attachments.map((f) => (
                <div
                  key={f.id}
                  data-testid={`attachment-${f.id}`}
                  className="border border-border rounded-lg p-3 bg-secondary/30 hover:border-primary/40"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm truncate" title={f.original_name}>
                        {f.original_name}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {(f.size_bytes / 1024).toFixed(1)} KB Â· {f.content_type || "file"}
                      </div>
                    </div>

                    <Paperclip className="h-4 w-4 shrink-0 text-muted-foreground" />
                  </div>

                  <div className="flex items-center gap-2 mt-3">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      data-testid={`attachment-preview-${f.id}`}
                      onClick={() => previewFile(f)}
                      className="h-8 px-2.5"
                      title="Preview file"
                    >
                      <Eye className="h-3.5 w-3.5 mr-1.5" />
                      Preview
                    </Button>

                    <Button
                      asChild
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-8 px-2.5"
                      title="Download file"
                    >
                      <a
                        href={`${api.defaults.baseURL}/attachments/${f.id}/download`}
                        data-testid={`attachment-download-${f.id}`}
                      >
                        <Download className="h-3.5 w-3.5 mr-1.5" />
                        Download
                      </a>
                    </Button>

                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      data-testid={`attachment-delete-${f.id}`}
                      onClick={() => deleteAttachment(f)}
                      disabled={deletingAttachmentId === f.id}
                      className="h-8 px-2.5 text-destructive hover:text-destructive hover:bg-destructive/10"
                      title="Delete file"
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                      {deletingAttachmentId === f.id ? "Deleting..." : "Delete"}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {previewAttachment && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
              role="dialog"
              aria-modal="true"
              aria-label={`Preview ${previewAttachment.original_name}`}
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                  closePreview();
                }
              }}
            >
              <div className="relative flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl border border-border bg-card shadow-2xl">
                <div className="flex items-center justify-between gap-4 border-b border-border px-4 py-3">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">
                      {previewAttachment.original_name}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {previewContentType || previewAttachment.content_type || "file"}
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={closePreview}
                    aria-label="Close preview"
                  >
                    Close
                  </Button>
                </div>

                <div className="flex min-h-[300px] items-center justify-center overflow-auto bg-black/20 p-4">
                  {previewLoading && (
                    <div className="text-sm text-muted-foreground">
                      Loading preview...
                    </div>
                  )}

                  {!previewLoading && previewUrl && previewContentType.startsWith("image/") && (
                    <img
                      src={previewUrl}
                      alt={previewAttachment.original_name}
                      className="max-h-[75vh] max-w-full rounded-md object-contain"
                    />
                  )}

                  {!previewLoading && previewUrl && previewContentType === "application/pdf" && (
                    <iframe
                      src={previewUrl}
                      title={previewAttachment.original_name}
                      className="h-[75vh] w-full rounded-md bg-white"
                    />
                  )}

                  {!previewLoading &&
                    previewUrl &&
                    !previewContentType.startsWith("image/") &&
                    previewContentType !== "application/pdf" && (
                      <div className="text-center">
                        <div className="text-sm font-medium">
                          Preview is not available for this file type.
                        </div>
                        <div className="mt-1 text-xs text-muted-foreground">
                          Download the file to open it with the appropriate application.
                        </div>

                        <Button
                          asChild
                          variant="outline"
                          size="sm"
                          className="mt-4"
                        >
                          <a
                            href={`${api.defaults.baseURL}/attachments/${previewAttachment.id}/download`}
                          >
                            <Download className="h-3.5 w-3.5 mr-1.5" />
                            Download file
                          </a>
                        </Button>
                      </div>
                    )}
                </div>
              </div>
            </div>
          )}

        </Tabs>

        {/* Right rail: actions */}
        <aside className="space-y-4">
          {(!clientAwaitingValidation || canShowClientIntakeActions) && !clientInternalDevelopmentStage && (
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Actions</div>
              {panelActions.client_confirmation_submit && !clientHasResponded && (
                <p className="mb-3 text-xs text-muted-foreground" data-testid="client-confirmation-requirement">
                  Add a comment or upload a file before submitting.
                </p>
              )}
              {panelActions.reference_submit && !accessClientHasResponded && (
                <p className="mb-3 text-xs text-muted-foreground" data-testid="reference-response-requirement">
                  Add a comment or upload a file before submitting.
                </p>
              )}
              {Object.keys(panelActions).length === 0 && <div className="text-xs text-muted-foreground">{user.role === ROLES.TEST_USER
  ? ticket.status === "open"
    ? "This ticket is awaiting validation."
    : ticket.status === "in_qa" && ticket.assigned_tester_id !== currentUserId
      ? "This fix is assigned to another Test User."
    : ticket.status === "in_qa" && ticket.assigned_tester_id !== currentUserId
      ? "This ticket is not assigned to your testing queue."
    : "No validation or assigned-testing actions are available at this stage."
  : ticket.status === "open" && user.role !== ROLES.TEST_USER
  ? "Awaiting validation by a Business Admin, Support Admin, or Test User."
  : ticket.status === "in_qa" && ![ROLES.BUSINESS_ADMIN, ROLES.SUPPORT_ADMIN, ROLES.TEST_USER].includes(user.role)
    ? "Waiting for Testing, Business, or Support Admin to complete testing."
    : isUnassignedSupportActor
  ? "This ticket has not yet been assigned to the support team by Business Admin. You have view-only access."
  : isNonOwningEngineer
    ? "This ticket is assigned to another Support Engineer. You have view-only access."
    : isAssignedBusinessUser
    ? "This ticket has been assigned to a Support Engineer. Administrative reassignment is required before business-side work can continue."
    : ticket.status === "client_confirmation_pending" && !clientHasResponded
      ? "Waiting for the client to add the requested information or supporting proof."
      : ticket.status === "on_hold" && !holdClientHasResponded
        ? "Work is paused until the client reviews this ticket and adds a public comment."
        : ticket.status === "in_progress" && !investigationClientHasResponded
          ? "Support review is in progress. Work can continue after the client adds a comment or supporting evidence."
          : ticket.status === "escalated" && !escalationClientHasResponded
            ? "Support is reviewing the ticket. Work can continue after the client adds a comment or supporting evidence."
            : ticket.status === "reference_pending" && !accessClientHasResponded
              ? "This ticket is Reference pending. Work can continue only after the client provides or confirms the required access."
                : "No actions available for your role at this step."}</div>}
              <div className="grid gap-2">
                {Object.entries(panelActions).map(([status, label]) => (
                  <Button key={status} data-testid={`action-${status}`}
                    disabled={
                      (status === "client_confirmation_submit" && clientConfirmationSaving) ||
                      (status === "reference_submit" && referenceResponseSaving) ||
                      (status === "reopened" && isAssignedQaTester && qaFailSaving) ||
                      (["in_progress", "dev_done", "dev_testing"].includes(ticket.status) && status === "in_progress") ||
                      (["dev_done", "dev_testing"].includes(ticket.status) && status === "dev_done") ||
                      (ticket.status === "dev_testing" && status === "dev_testing") ||
                      (ticket.status === "on_hold" && status === "on_hold")
                    }
                    title={status === "no_issue"
                    ? "Validate that the product works as expected. Available while the ticket is Open."
                    : !actions[status] && !canOverrideTicketAction(user.role, ticket, currentUserId) ? getUnavailableActionMessage(status, ticket.status) : undefined}
                    onClick={() => {
                      if (status === "client_confirmation_submit") {
                        void submitClientConfirmation();
                        return;
                      }
                      if (status === "reference_submit") {
                        void submitReferenceResponse();
                        return;
                      }
                      if (status === "reopened" && isAssignedQaTester) {
                        void failQaAndReopen();
                        return;
                      }
                      openAction(status, label);
                    }}
                    className="justify-start text-sm bg-secondary/70 hover:bg-primary/15 hover:text-primary text-foreground border border-border">
                    <ActionIcon status={status} className="h-3.5 w-3.5 mr-2" />
                    {label}
                  </Button>
                ))}
              </div>
              {[ROLES.TEST_USER, ROLES.SUPPORT_ADMIN, ROLES.BUSINESS_ADMIN].includes(user.role) && (
                <p className="mt-3 text-xs text-muted-foreground">
                All actions are clickable. Actions unavailable at this stage explain
                the workflow requirements instead of changing the ticket.
                </p>
              )}
            </div>
          )}

          <div className="rounded-xl border border-border bg-card p-4 space-y-2 text-xs">
            <div className="mb-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              Client and ownership
            </div>

            <Row k="Client" v={clientName || <Empty />} />
            <Row k="Raised by" v={<Person person={people.raised_by} />} />
            <Row
              k="Client Admin"
              v={<Person person={people.client_admins?.[0]} />}
            />
            <Row
              k="Business Admin"
              v={<Person person={businessAdmin} />}
            />
            <Row
              k="Assigned engineer"
              v={<Person person={people.assigned_engineer} />}
            />

            <div className="mt-3 border-t border-border pt-3">
              <Row
                k="Current owner"
                v={<Person person={people.current_owner} />}
              />
            </div>
          </div>
        </aside>
      </div>

      {/* Action modal */}
      {actionModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto"
          onClick={() => setActionModal(null)}
          onKeyDown={(e) => {
            if (e.key === "Escape") setActionModal(null);
            if (
              e.key === "Enter" &&
              (e.ctrlKey || e.metaKey) &&
              e.target.tagName === "TEXTAREA"
            ) {
              e.preventDefault();
              submitAction();
            }
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="glass w-full max-w-lg my-8 rounded-xl border border-border p-6 space-y-4 max-h-[calc(100vh-4rem)] overflow-y-auto scrollbar-thin"
            data-testid="action-modal"
            tabIndex={-1}
          >
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Confirm action</div>
              <h3 className="font-display text-xl font-bold mt-1">{actionModal.label}</h3>
              {actionModal.isNoIssue && (
                <p className="mt-2 text-sm text-muted-foreground">
                  Confirm that you validated the reported issue and the product works as expected.
                  This records a No Issue outcome using the existing terminal ticket status.
                </p>
              )}
            </div>
            {actionModal.prRequiredWarning && (
              <p className="rounded-lg border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
                Please raise the PR before marking the ticket Dev Done. Add the PR reference and URL in the Pull Request &amp; Merge section.
              </p>
            )}
            {actionModal.status === "assigned" && (
              <div className="space-y-3">
                <div>
                  <Label>Assign to</Label>
                  <Select
                    value={actionState.assignment_type}
                    onValueChange={(value) => setActionState((state) => ({
                      ...state,
                      assignment_type: value,
                      assigned_engineer_id: "",
                      assigned_tester_id: "",
                    }))}
                  >
                    <SelectTrigger data-testid="assign-type-select">
                      <SelectValue placeholder="Choose Tester or Engineer" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tester">Tester</SelectItem>
                      <SelectItem value="engineer">Engineer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {actionState.assignment_type === "engineer" && (
                  <div>
                    <Label>Engineer</Label>
                    <Select
                      value={actionState.assigned_engineer_id}
                      onValueChange={(value) => setActionState((state) => ({ ...state, assigned_engineer_id: value }))}
                    >
                      <SelectTrigger data-testid="assign-engineer-select">
                        <SelectValue placeholder="Pick engineer" />
                      </SelectTrigger>
                      <SelectContent>
                        {engineers.filter((engineer) => hasDisplayValue(engineer.name) || hasDisplayValue(engineer.email)).map((engineer) => (
                          <SelectItem key={engineer.id} value={engineer.id}>
                            {joinDisplayValues(engineer.name, engineer.email)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {engineers.length === 0 && (
                      <p className="mt-2 text-xs text-muted-foreground">No active Engineers found.</p>
                    )}
                  </div>
                )}
                {actionState.assignment_type === "tester" && (
                  <div>
                    <Label>Tester</Label>
                    <Select
                      value={actionState.assigned_tester_id}
                      onValueChange={(value) => setActionState((state) => ({ ...state, assigned_tester_id: value }))}
                    >
                      <SelectTrigger data-testid="assign-tester-select">
                        <SelectValue placeholder="Pick tester" />
                      </SelectTrigger>
                      <SelectContent>
                        {testers.map((tester) => (
                          <SelectItem key={tester.id} value={tester.id}>
                            {joinDisplayValues(tester.name, "Test User", tester.email)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {testers.length === 0 && (
                      <TestUsersDirectoryMessage
                        loading={testersLoading}
                        error={testersError}
                        onRetry={() => setTestersReloadKey((key) => key + 1)}
                      />
                    )}
                  </div>
                )}
              </div>
            )}
            {actionModal.status === "assign_tester" && (
              <div>
                <Label>Assign to Tester</Label>
                <Select
                  value={actionState.assigned_tester_id}
                  onValueChange={(value) =>
                    setActionState((state) => ({
                      ...state,
                      assigned_tester_id: value,
                    }))
                  }
                >
                  <SelectTrigger data-testid="assign-tester-select">
                    <SelectValue placeholder="Pick a tester" />
                  </SelectTrigger>
                  <SelectContent>
                    {testers.map((tester) => (
                      <SelectItem key={tester.id} value={tester.id}>
                        {joinDisplayValues(tester.name, "Test User", tester.email)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {testers.length === 0 && (
                  <TestUsersDirectoryMessage
                    loading={testersLoading}
                    error={testersError}
                    onRetry={() => setTestersReloadKey((key) => key + 1)}
                  />
                )}
              </div>
            )}
            {actionModal.status === "in_qa" && (
              <div>
                <Label>Assign to Tester</Label>
                <Select
                  value={actionState.assigned_tester_id}
                  onValueChange={(value) =>
                    setActionState((state) => ({
                      ...state,
                      assigned_tester_id: value,
                    }))
                  }
                >
                  <SelectTrigger data-testid="in-qa-tester-select">
                    <SelectValue placeholder="Pick a tester" />
                  </SelectTrigger>
                  <SelectContent>
                    {testers.map((tester) => (
                      <SelectItem key={tester.id} value={tester.id}>
                        {joinDisplayValues(tester.name, "Test User", tester.email)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {testers.length === 0 && (
                  <TestUsersDirectoryMessage
                    loading={testersLoading}
                    error={testersError}
                    onRetry={() => setTestersReloadKey((key) => key + 1)}
                  />
                )}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setActionModal(null);
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                data-testid="action-confirm"
                onClick={actionModal.prRequiredWarning
                  ? () => {
                      setActionModal(null);
                      window.requestAnimationFrame(() => {
                        document.getElementById("ticket-pr-section")?.scrollIntoView({ behavior: "smooth", block: "center" });
                      });
                    }
                  : submitAction}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                {actionModal.prRequiredWarning
                  ? "Go to PR Details"
                  : actionModal.status === "in_qa" ? "Assign Tester" : "Confirm"}
              </Button>
            </div>
          </div>
        </div>
      )}

      <TicketEditDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        ticket={ticket}
        role={user.role}
        onSaved={() => {
          window.dispatchEvent(new Event("helpdesk-ticket-changed"));
          load();
        }}
      />
    </div>
  );
}

function TicketDetailSkeleton() {
  return (
    <div
      className="space-y-6"
      data-testid="ticket-detail-skeleton"
    >
      <div className="flex items-center gap-2">
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-4 w-3" />
        <Skeleton className="h-4 w-16" />
        <Skeleton className="h-4 w-3" />
        <Skeleton className="h-4 w-32" />
      </div>

      <div className="glass rounded-xl border border-border p-6">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex-1 space-y-4">
            <div className="flex gap-2">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-5 w-16 rounded-full" />
              <Skeleton className="h-5 w-24 rounded-full" />
            </div>

            <Skeleton className="h-9 w-full max-w-xl" />

            <div className="flex flex-wrap gap-3">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-36" />
            </div>
          </div>

          <div className="w-full space-y-2 lg:w-64">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
        <div className="space-y-4">
          <Skeleton className="h-10 w-full max-w-2xl" />

          <div className="rounded-xl border border-border bg-card p-5">
            <div className="space-y-4">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-[92%]" />
              <Skeleton className="h-4 w-[76%]" />

              <div className="grid grid-cols-1 gap-4 pt-3 md:grid-cols-2">
                <Skeleton className="h-32 w-full" />
                <Skeleton className="h-32 w-full" />
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <Skeleton className="h-48 w-full rounded-xl" />
          <Skeleton className="h-64 w-full rounded-xl" />
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">{title}</div>
      <div className="text-sm whitespace-pre-wrap">{children}</div>
    </div>
  );
}
function Empty() { return null; }
function Meta({ label, value }) {
  const shownValue = displayValue(value);
  if (!hasDisplayValue(shownValue)) return null;
  return <span><span className="uppercase tracking-widest text-[10px] mr-1 opacity-70">{label}</span><span className="text-foreground">{shownValue}</span></span>;
}
function Row({ k, v }) {
  const shownValue = displayValue(v);
  if (!hasDisplayValue(shownValue)) return null;
  return <div className="flex justify-between gap-2"><span className="text-muted-foreground">{k}</span><span className="text-foreground text-right">{shownValue}</span></div>;
}

function ActionIcon({ status, className }) {
  if (["closed", "production"].includes(status)) return <CheckCircle2 className={className} />;
  if (status === "invalid") return <XCircle className={className} />;
  if (["assigned"].includes(status)) return <User2 className={className} />;
  if (["in_progress", "in_progress", "in_qa"].includes(status)) return <PlayCircle className={className} />;
  if (["reopened"].includes(status)) return <RotateCcw className={className} />;
  return <PlayCircle className={className} />;
}

function TestUsersDirectoryMessage({ loading, error, onRetry }) {
  if (loading) {
    return <p className="mt-2 text-xs text-muted-foreground">Loading Test Users…</p>;
  }
  if (error) {
    return (
      <div className="mt-2 flex items-center gap-2 text-xs text-destructive">
        <span>Could not load Test Users: {error}</span>
        <Button type="button" variant="outline" size="sm" onClick={onRetry}>
          Retry
        </Button>
      </div>
    );
  }
  return <p className="mt-2 text-xs text-muted-foreground">No active Test Users found.</p>;
}

function Person({ person }) {
  if (!person) return <Empty />;

  const name = firstDisplayValue(person.name, person.email);
  if (!name) return null;

  return (
    <span title={person.email || ""}>
      {name}
    </span>
  );
}

const SLA_EVENT_LABEL = {
  "sla.reminder": "SLA reminder â†’ engineer",
  "sla.escalation": "SLA escalated â†’ manager",
  "sla.manager_reminder": "SLA reminder â†’ manager",
};

function formatEventValue(value) {
  if (value === null || value === undefined || value === "") return "";

  if (typeof value !== "object") {
    return String(value).replace(/_/g, " ");
  }

  return "";
}

function formatStatusValue(value, clientStatusView = false) {
  const status = value && typeof value === "object" ? value.status : value;
  if (!status) return "";
  const displayStatus = clientStatusView
    ? getClientDisplayStatus({ status })
    : getTicketDisplayStatus({ status });
  return STATUS_LABEL[displayStatus] || "—";
}

function formatBytes(bytes) {
  const value = Number(bytes);

  if (!Number.isFinite(value) || value <= 0) return "";

  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;

  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function findEventAttachment(event, attachments = []) {
  const name =
    event?.to_value?.name ||
    event?.to_value?.original_name ||
    "";

  if (!name) return null;

  return (
    attachments.find(
      (attachment) =>
        attachment.original_name === name ||
        attachment.filename === name
    ) || null
  );
}

function formatActivityStatement(event, clientStatusView = false) {
  const type = String(event.event_type || "").toLowerCase();
  const toValue = event.to_value;
  const fromValue = event.from_value;

  if (clientStatusView && type === "ticket.reopened") {
    return "Status changed to In Progress.";
  }

  if (type === "ticket.created") {
    const number = toValue?.number;
    const status = formatStatusValue(toValue?.status, clientStatusView);

    return number
      ? `Ticket ${number} was created${status ? ` with status ${status}.` : "."}`
      : "Ticket was created.";
  }

  if (type === "attachment.added") {
    const name = toValue?.name || "a file";
    const size = formatBytes(toValue?.size);

    return `Attachment "${name}" was added${size ? ` (${size})` : ""}.`;
  }

  if (type === "attachment.downloaded") {
    const name = toValue?.name || "an attachment";

    return `Attachment "${name}" was downloaded.`;
  }

  if (type === "attachment.deleted") {
    const name = toValue?.name || "an attachment";

    return `Attachment "${name}" was deleted.`;
  }

  if (type.startsWith("status.")) {
    const newStatus = formatStatusValue(toValue, clientStatusView);
    const oldStatus = formatStatusValue(fromValue, clientStatusView);

    return oldStatus && newStatus
      ? `Status changed from ${oldStatus} to ${newStatus}.`
      : newStatus
        ? `Status changed to ${newStatus}.`
        : "Ticket status was changed.";
  }

  const label =
    SLA_EVENT_LABEL[type] ||
    type.replace(/[._]/g, " ").replace(/^./, (c) => c.toUpperCase());

  return label;
}

function Timeline({ events, attachments = [], clientStatusView = false }) {
  const activityEvents = events.filter((event) => !isCommentActivityEvent(event));
  if (!activityEvents.length) {
    return <div className="text-xs text-muted-foreground">No events yet.</div>;
  }

  return (
    <ol className="relative border-l border-border ml-2 space-y-4">
      {activityEvents.map((e) => (
        <li key={e.id} className="ml-4">
          <span className="absolute -left-1.5 h-3 w-3 rounded-full bg-primary border-2 border-background" />

          <div className="text-xs text-muted-foreground">
            <span className="text-foreground font-medium">
              {e.actor_name || "System"}
            </span>
            <span> - {formatDateTime(e.created_at)}</span>
          </div>

          <div className="text-sm mt-1">
            {(() => {
              const attachment = findEventAttachment(e, attachments);
              const name =
                e?.to_value?.name ||
                e?.to_value?.original_name ||
                "";

              if (
                attachment &&
                (String(e.event_type || "").toLowerCase() === "attachment.added" ||
                  String(e.event_type || "").toLowerCase() === "attachment.downloaded")
              ) {
                const statementPrefix =
                  String(e.event_type || "").toLowerCase() === "attachment.added"
                    ? "Attachment"
                    : "Attachment";

                const size = formatBytes(e?.to_value?.size);

                return (
                  <>
                    {statementPrefix}{" "}
                    <a
                      href={`${api.defaults.baseURL}/attachments/${attachment.id}/download`}
                      target="_blank"
                      rel="noreferrer"
                      className="font-medium text-primary underline-offset-2 hover:underline"
                    >
                      "{name || attachment.original_name || attachment.filename}"
                    </a>
                    {String(e.event_type || "").toLowerCase() === "attachment.added" && size
                      ? ` was added (${size}).`
                      : String(e.event_type || "").toLowerCase() === "attachment.downloaded"
                        ? " was downloaded."
                        : "."}
                  </>
                );
              }

              return formatActivityStatement(e, clientStatusView);
            })()}
          </div>

        </li>
      ))}
    </ol>
  );
}

function WorklogsPane({ ticketId, worklogs, onChanged, canLog }) {
  const [activity, setActivity] = useState("");
  const [minutes, setMinutes] = useState(30);
  const [billable, setBillable] = useState(false);

  const submit = async () => {
    if (!activity.trim()) return toast.error("Activity is required");
    try {
      await api.post(`/tickets/${ticketId}/worklogs`, { activity, minutes: Number(minutes), billable });
      setActivity("");
      setMinutes(30);
      toast.success("Worklog added");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-3">
      {worklogs.length === 0 && <div className="text-xs text-muted-foreground">No worklogs yet.</div>}
      <div className="grid grid-cols-1 gap-2">
        {worklogs.map((w) => (
          <div key={w.id} className="border border-border rounded-lg p-3 bg-secondary/30 text-sm">
            <div className="flex items-center justify-between">
              <span className="font-semibold">{w.engineer_name}</span>
              <span className="font-mono text-xs">{w.minutes} min Â· {w.billable ? "billable" : "non-billable"}</span>
            </div>
            <div>{w.activity}</div>
            <div className="text-xs text-muted-foreground">{formatDateTime(w.created_at)}</div>
          </div>
        ))}
      </div>
      {canLog && (
        <div className="border-t border-border pt-3 grid grid-cols-1 md:grid-cols-[1fr_120px_140px_auto] gap-2 items-end">
          <div>
            <Label>Activity</Label>
            <Input data-testid="worklog-activity" value={activity} onChange={(e) => setActivity(e.target.value)} placeholder="What did you do?" />
          </div>
          <div>
            <Label>Minutes</Label>
            <Input data-testid="worklog-minutes" type="number" min={1} value={minutes} onChange={(e) => setMinutes(e.target.value)} />
          </div>
          <div>
            <Label>Billable</Label>
            <Select value={billable ? "yes" : "no"} onValueChange={(v) => setBillable(v === "yes")}>
              <SelectTrigger data-testid="worklog-billable"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="no">No</SelectItem>
                <SelectItem value="yes">Yes</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button data-testid="worklog-add" onClick={submit} className="bg-primary text-primary-foreground hover:bg-primary/90">Add</Button>
        </div>
      )}
    </div>
  );
}
